import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { getSafeInternalPath } from '../../utils/urlParams';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import SignInForm from '../../components/SignInForm';
import PublicNav from '../../components/PublicNav';
import PublicFooter from '../../components/PublicFooter';

const WELCOME_GREETINGS = [
  'Welcome back.',
  'Good to see you again.',
  "Let's forecast some markets.",
  'Sharpening the forecasts…',
  'Ready to compete?',
  'Your edge awaits.',
];

const WELCOME_DURATION_MS = 1500;

export default function SignIn() {
  usePageTitle('Sign in');
  const navigate = useNavigate();
  const location = useLocation();
  const [welcomeMessage, setWelcomeMessage] = useState(null);

  // Where to land after login. Priority: the URL the user originally asked
  // for, carried either as router state (SPA redirect from PrivateRoute) or
  // as a validated ?next= param (hard redirect from the 401 interceptor).
  // Fallback: the dashboard.
  const from = location.state?.from;
  const fromPath = from ? `${from.pathname || ''}${from.search || ''}${from.hash || ''}` : null;
  const nextParam = getSafeInternalPath(new URLSearchParams(location.search).get('next'));
  const redirectTarget = getSafeInternalPath(fromPath) || nextParam || '/dashboard';

  useEffect(() => {
    if (!welcomeMessage) return;
    const t = setTimeout(() => navigate(redirectTarget, { replace: true }), WELCOME_DURATION_MS);
    return () => clearTimeout(t);
  }, [welcomeMessage, navigate, redirectTarget]);

  const handleAuthenticated = () => {
    // Cosmetic greeting pick; randomness is not security-relevant here.
    // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
    const msg = WELCOME_GREETINGS[Math.floor(Math.random() * WELCOME_GREETINGS.length)];
    setWelcomeMessage(msg);
  };

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav/>

      <main className="flex-1 flex items-center justify-center w-full">
        <div className="max-w-md mx-auto px-6 py-16 w-full">
          <div className="text-center">
            {!welcomeMessage && (
              <>
                <h1 className="text-xl sm:text-2xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white">
                  Welcome <span className="italic text-teal-700 dark:text-teal-400">back</span>.
                </h1>
                <p className="mt-3 text-sm text-slate-600 dark:text-gray-300">
                  Sign in to access the Predico dashboard.
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                  For authorized members only.
                </p>
              </>
            )}
          </div>

          {welcomeMessage ? (
            <div role="status" aria-live="polite" className="mt-12 text-center">
              <p className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-white">
                {welcomeMessage}
              </p>
              <div className="mt-6 flex justify-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-teal-700 dark:bg-teal-400 animate-bounce" style={{animationDelay: '0ms'}}/>
                <span className="h-2 w-2 rounded-full bg-teal-700 dark:bg-teal-400 animate-bounce" style={{animationDelay: '150ms'}}/>
                <span className="h-2 w-2 rounded-full bg-teal-700 dark:bg-teal-400 animate-bounce" style={{animationDelay: '300ms'}}/>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-10 rounded-2xl bg-surface dark:bg-gray-870 border border-slate-900/10 dark:border-gray-100/10 p-6 sm:p-8">
                <SignInForm onAuthenticated={handleAuthenticated}/>
              </div>

              <div className="mt-6 text-center">
                <Link to="/"
                      className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition">
                  <ArrowLeftIcon className="h-4 w-4"/>
                  Back to homepage
                </Link>
              </div>
            </>
          )}
        </div>
      </main>

      <PublicFooter/>
    </div>
  );
}
